// Local TypeScript mirrors of the CRM's Zod contracts (crm/packages/contracts).
//
// The shared contracts package (@crm/contracts) is not yet published/vendored
// into this repo (Fase 0 leaves it pending), so we transcribe the minimal set
// of types this SPA consumes. Field names MUST match the CRM schemas exactly —
// see crm/packages/contracts/src/ticket.ts.

export type TicketState = "open" | "closed";

// crm: Market — the operating markets the CRM ships. Used to constrain the
// ticket list `market` filter (and reused by other CRM-scoped views).
export type Market = "ES" | "IT" | "FR";

// crm: PhoneRecordingStatus (crm/packages/contracts/src/phone.ts). Recording
// lifecycle of a call; only 'available' means a signed URL can be fetched.
export type PhoneRecordingStatus =
  | "none"
  | "pending"
  | "available"
  | "failed"
  | "not_recorded";

export type TicketPriority = "low" | "medium" | "high" | "urgent";

// crm: TicketPipeline
export type TicketPipeline = {
  id: string;
  label: string;
  position: number;
  market: string | null;
  archived: boolean;
};

// crm: TicketStage
export type TicketStage = {
  id: string;
  pipelineId: string;
  label: string;
  position: number;
  ticketState: TicketState;
  archived: boolean;
};

// crm: TicketSummary
export type TicketSummary = {
  id: string;
  pipelineId: string;
  pipelineLabel: string;
  stageId: string;
  stageLabel: string;
  stageState: TicketState;
  subject: string;
  leadId: string | null;
  assignedTo: string | null;
  priority: TicketPriority | null;
  market: string | null;
  source: string;
  // Soft-delete. Solo puede llegar `true` si el listado pidió
  // `archived: 'include' | 'only'`; con el default ('exclude') el CRM ya los ha
  // filtrado server-side y aquí siempre es false.
  archived: boolean;
  // Tipificación de Ops en 2 niveles (2026-07-16, espejo de HubSpot): Opción 1
  // global (`incidentType`) + Opción 2 por pipeline (`typification`). Catálogo y
  // helpers en `crmTicketTypification.ts`. Valores literales de HubSpot.
  incidentType: string | null;
  typification: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  lastActivityAt: string;
};

// crm: TicketsListResponse (GET /tickets)
export type TicketsListResponse = {
  items: TicketSummary[];
  total: number;
  page: number;
  pageSize: number;
};

// crm: eje `archived` de TicketsListQuery. ORTOGONAL a `state` (open/closed):
// un ticket archivado conserva su stage, así que no puede colapsarse en `state`.
// Filtrar por `state: 'open'` NO excluye archivados y viceversa.
export type TicketsArchivedFilter = "exclude" | "include" | "only";

// crm: TicketsListQuery (query params of GET /tickets). All optional; the client
// builds the query string with URLSearchParams. `q` is the free-text search over
// subject/description (case-insensitive, 1..200 chars) added in a parallel CRM
// branch — included here so the tab can wire the search box immediately.
export type TicketsListFilters = {
  leadId?: string;
  pipelineId?: string;
  stageId?: string;
  state?: TicketState;
  assignedTo?: string;
  market?: Market;
  // Visibilidad del soft-delete. Omitirlo = 'exclude' server-side (solo vivos).
  archived?: TicketsArchivedFilter;
  // Filtro exacto por tipificación (Opción 1 / Opción 2). Ambos soportados por
  // el backend en GET /tickets (tickets.repo list: eq incidentType/typification).
  incidentType?: string;
  typification?: string;
  q?: string;
  page?: number;
  limit?: number;
};

// ---- Board (Kanban de tickets, Fase 6) ----

// crm: TicketsBoardQuery (query params of GET /tickets/board). `pipelineId` is
// REQUIRED (the board is always scoped to one pipeline); the rest are optional.
// `limitPerColumn` defaults to 50 server-side (1..500). Built with URLSearchParams.
export type TicketsBoardFilters = {
  pipelineId: string;
  market?: Market;
  assignedTo?: string;
  limitPerColumn?: number;
};

// crm: TicketBoardColumn (one stage). `hasMore` is true when the stage holds
// more tickets than `limitPerColumn` returned (the extra rows are not shipped).
export type TicketBoardColumn = {
  stageId: string;
  stageLabel: string;
  position: number;
  ticketState: TicketState;
  tickets: TicketSummary[];
  hasMore: boolean;
};

// crm: TicketsBoardResponse (GET /tickets/board). Columns are pre-sorted by
// stage position server-side. 404 pipeline_not_found if the pipeline is unknown.
export type TicketsBoardResponse = {
  pipelineId: string;
  pipelineLabel: string;
  columns: TicketBoardColumn[];
};

// crm: MoveTicketBody (POST /tickets/:id/move). Only the destination stage; the
// CRM validates it exists AND belongs to the ticket's pipeline (else 400
// invalid_stage). No position field — ordering within a column is by updatedAt.
export type MoveTicketBody = { stageId: string };

// crm: MoveTicketPipelineBody (POST /tickets/:id/move-pipeline). Moves the
// ticket to ANOTHER pipeline. `stageId` is optional: omitted, the CRM lands the
// ticket on the destination's first open stage; present, it must belong to the
// destination pipeline (else 400 invalid_stage). 400 invalid_pipeline if the
// pipeline is unknown/archived or has no open stage.
export type MoveTicketPipelineBody = { pipelineId: string; stageId?: string };

// crm: AssignTicketBody (POST /tickets/:id/assign). `assignee` is a valid email
// or null to unassign; the CRM enforces the email format (Zod .email()).
export type AssignTicketBody = { assignee: string | null };

// crm: LeadSummary (hydrated on TicketDetail.lead). Only the fields the ticket
// detail renders are transcribed; the CRM object carries many more.
export type TicketLeadSummary = {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  market: string | null;
  assignedTo: string | null;
  assignedToName: string | null;
};

// crm: TicketDetail (GET /tickets/:id). TicketSummary plus description, the
// hydrated lead (null when leadId is null) and audit fields.
export type TicketDetail = TicketSummary & {
  description: string | null;
  hubspotId: string | null;
  lead: TicketLeadSummary | null;
};

// Límites de adjuntos en notas de ticket (compartidos con el email en el CRM,
// contracts/src/email.ts). Replicados aquí como source-of-truth del cliente para
// validar en el composer ANTES de subir (mensajes en español). El CRM revalida.
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB por archivo
export const MAX_ATTACHMENTS_TOTAL_BYTES = 20 * 1024 * 1024; // 20 MB en total

// crm: TicketAttachment — adjunto YA persistido de una nota (metadata; el blob
// vive en un bucket privado GCS y se descarga SIEMPRE vía API autenticada, sin
// URLs públicas).
export type TicketAttachment = {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
};

// Tope del HTML de una nota enriquecida (crm: MAX_NOTE_HTML_CHARS,
// contracts/src/comment.ts). Las imágenes NO viajan inline en el HTML (se
// extraen a adjuntos con src="attachment:filename"), así que 64 KB de markup
// es holgado; el CRM rechaza (400) por encima. Validamos antes de enviar.
export const MAX_NOTE_HTML_CHARS = 65536;

// Tope del body PLANO de una nota de ticket (crm: CreateTicketCommentBody,
// z.string().max(10000)). Compartido por el maxLength del textarea y la
// validación pre-envío (la derivación rico→plano puede superar lo que el
// maxLength del textarea permite teclear).
export const MAX_NOTE_BODY_CHARS = 10000;

// crm: TicketComment (GET /tickets/:id/comments -> { items }). Ticket-scoped
// notes (subject_kind='ticket'). deletedAt/deletedBy only reach admins.
export type TicketComment = {
  id: string;
  body: string;
  // Nota ENRIQUECIDA (2026-07-16): HTML del editor, imágenes como
  // src="attachment:filename". Null/ausente = nota de texto sencillo.
  bodyHtml?: string | null;
  actor: string;
  actorName: string | null;
  actorPictureUrl: string | null;
  createdAt: string;
  deletedAt?: string | null;
  deletedBy?: string | null;
  // Chincheta (lead_comments.pinned_at). MISMA columna que las notas de lead:
  // fijar aquí también la fija en el timeline del lead asociado, y viceversa.
  // El listado del CRM devuelve las fijadas ARRIBA (pinned_at desc nulls last).
  pinnedAt?: string | null;
  // Documentos adjuntos de la nota (default [] en el contrato del CRM).
  attachments?: TicketAttachment[];
};

// crm: CreateTicketCommentBody (POST /tickets/:id/comments). Una nota necesita
// texto (0..10000) O al menos un adjunto (base64 inline, patrón email).
export type NewTicketAttachment = {
  filename: string;
  contentType: string;
  contentBase64: string;
};
export type CreateTicketCommentBody = {
  body: string;
  // Cuerpo enriquecido opcional (<= MAX_NOTE_HTML_CHARS). `body` viaja SIEMPRE
  // con la derivación a texto plano (tokens de imagen incluidos): alimenta
  // snippets, búsqueda e historial en el CRM.
  bodyHtml?: string | null;
  attachments?: NewTicketAttachment[];
};

// crm: TicketActivityEntry (GET /tickets/:id/activity -> { items }). Audit log
// of the ticket: 'action' rows (activity_log), 'stage' moves and 'assignment'
// changes. `type` is a closed union; `action` is free string on the CRM side.
export type TicketActivityType = "action" | "stage" | "assignment";

export type TicketActivityEntry = {
  type: TicketActivityType;
  action: string | null;
  actor: string;
  occurredAt: string;
  detail: Record<string, unknown> | null;
  fromStage: string | null;
  toStage: string | null;
  fromAssignee: string | null;
  toAssignee: string | null;
};

// crm: CreateTicketBody (POST /tickets). Required: pipelineId, subject.
export type CreateTicketBody = {
  pipelineId: string;
  subject: string;
  stageId?: string;
  description?: string | null;
  leadId?: string | null;
  assignee?: string | null;
  priority?: TicketPriority | null;
  // Tipificación opcional (validada contra el catálogo por el backend, escopada
  // al pipeline del ticket para la Opción 2).
  incidentType?: string | null;
  typification?: string | null;
};

// POST /tickets -> 201 { id }
export type CreateTicketResponse = { id: string };

// ===========================================================================
// Phone calls (Fase 5 — global Calls tab). Mirrors crm/packages/contracts
// src/phoneCalls.ts (GET /phone/calls) and src/phone.ts (PhoneCallStatus /
// PhoneCallDirection), plus AssignableAdvisor from src/task.ts (GET /advisors).
// ===========================================================================

// crm: PhoneCallDirectionFilter (phoneCalls.ts). The only directions accepted
// as a FILTER value. Note the list ITEM's `direction` is free string on the CRM
// side ('inbound' | 'outbound' | 'unknown' for migrated history), so PhoneCallListItem
// keeps it as string; this closed union constrains only the filter dropdown.
export type PhoneCallDirectionFilter = "inbound" | "outbound";

// crm: PhoneCallStatus (phone.ts). Final orchestrator statuses. Used to label
// the status Pill; the list item's `status` stays string (migrated history may
// carry values outside this set).
export type PhoneCallStatus =
  | "queued"
  | "ringing"
  | "in_progress"
  | "completed"
  | "no_answer"
  | "busy"
  | "failed"
  | "voicemail"
  | "canceled";

// crm: PhoneCallListItem (GET /phone/calls -> items[]). Enriched with the lead
// (market/name/email) and agent (name) via JOINs. `direction`/`status`/
// `recordingStatus` are free strings server-side (migrated history) — the view
// narrows them for display but must not assume the closed unions.
export type PhoneCallListItem = {
  callId: string;
  occurredAt: string; // ISO 8601
  direction: string; // inbound|outbound|unknown
  status: string;
  counterpartE164: string | null;
  phoneLast9: string | null;
  durationSec: number | null;
  talkingSec: number | null;
  recordingStatus: string | null;
  agentEmail: string | null;
  agentName: string | null;
  leadId: string | null; // hex-32 ulid — null if the call resolved no lead
  leadName: string | null;
  leadEmail: string | null;
  market: string | null;
};

// crm: PhoneCallsListResponse (GET /phone/calls). NOT page/total based: keyset
// cursor navigation — `hasMore` + `nextCursor` drive prev/next. The endpoint
// also returns page/pageSize/totalApprox, but this SPA navigates purely by
// cursor and never reads them, so they are omitted from the mirror.
export type PhoneCallsListResponse = {
  items: PhoneCallListItem[];
  hasMore: boolean;
  nextCursor: string | null;
};

// crm: PhoneCallsListQuery (query params of GET /phone/calls). All filters are
// optional and combinable (AND). `dateFrom`/`dateTo` accept YYYY-MM-DD; `phone`
// is a SARGABLE match (E.164 exact if it starts with '+', else last-9 exact/
// suffix). `cursor` is the opaque keyset cursor from the previous page.
export type PhoneCallsListFilters = {
  dateFrom?: string;
  dateTo?: string;
  agentEmail?: string;
  direction?: PhoneCallDirectionFilter;
  status?: PhoneCallStatus;
  market?: Market;
  phone?: string;
  pageSize?: number;
  sortDir?: "asc" | "desc";
  cursor?: string;
};

// crm: AssignableAdvisor (GET /advisors -> { items }). The agent dropdown of the
// Calls tab. NOTE: /advisors is requireRole('admin','sales') in the CRM — an ops
// user gets 403, so the API layer treats it fail-soft (empty list, free-text
// email fallback) rather than surfacing a fatal error.
export type CrmAdvisor = {
  email: string;
  name: string | null;
  pictureUrl: string | null;
  role: "admin" | "sales" | "ops" | "viewer";
  defaultMarket: string | null;
};

// ===========================================================================
// Timeline (Fase 3 — LeadTimeline panel). Mirrors crm/packages/contracts
// src/comment.ts: Timeline / TimelineEntry (discriminated union on `type`).
// ===========================================================================

// crm: CommentKind
export type CommentKind =
  | "nota_interna"
  | "llamada"
  | "email_enviado"
  | "email_recibido"
  | "whatsapp_enviado"
  | "whatsapp_recibido"
  | "sistema";

// Shared base of every timeline entry.
type TimelineEntryBase = {
  occurredAt: string;
  actor: string | null;
  actorName: string | null;
  actorPictureUrl: string | null;
};

// crm: TimelineMoveEntry (type='workflow_move')
export type TimelineMoveEntry = TimelineEntryBase & {
  type: "workflow_move";
  moveId?: string | null;
  fromTag: string | null;
  toTag: string;
  fromTagLabel: string | null;
  toTagLabel: string | null;
};

// crm: TimelineCommentEntry (type='comment'). Covers plain notes, calls
// (kind='llamada' with phoneCallId) and ticket notes (ticketId set).
export type TimelineCommentEntry = TimelineEntryBase & {
  type: "comment";
  commentId: string;
  kind: CommentKind;
  body: string;
  bodyHtml?: string | null;
  phoneCallId?: string | null;
  recordingStatus?: PhoneRecordingStatus | null;
  callNote?: string | null;
  mediaUrl?: string | null;
  mediaType?: string | null;
  sharedFromLeadId?: string | null;
  sharedFromLeadName?: string | null;
  pinnedAt?: string | null;
  ticketId?: string | null;
  ticketSubject?: string | null;
  // Adjuntos de la nota (capturas pegadas incluidas). El body los referencia
  // por filename con tokens `![alt](attachment:filename)`; el front casa
  // token↔adjunto y descarga el blob autenticado. Misma forma que
  // TicketAttachment (crm: CommentAttachment — tabla compartida).
  attachments?: TicketAttachment[];
  // Nota editada por su autor/un admin: ISO de la última edición, o null. El
  // cuerpo previo queda archivado en comment_activity_log (auditoría del CRM).
  editedAt?: string | null;
  // Nota ARCHIVADA (nota_interna retirada por su autor o un admin). El
  // timeline solo la devuelve a esos dos (el resto del equipo no la ve);
  // llega marcada para pintarla atenuada. La fila y su historial se conservan.
  deletedAt?: string | null;
  deletedBy?: string | null;
};

// crm: UpdateCommentBody (PATCH /leads/:id/comments/:commentId). Reescribe el
// cuerpo de una nota propia (o de cualquiera, si eres admin). `attachments`
// AÑADE adjuntos nuevos; los ya persistidos se conservan y sus tokens siguen
// resolviendo. bodyHtml omitido/null ⇒ la nota vuelve a texto sencillo. El
// cuerpo previo se archiva en comment_activity_log antes de sobrescribir.
export type UpdateCommentBody = {
  body: string;
  bodyHtml?: string | null;
  attachments?: NewTicketAttachment[];
};

// crm: TimelineObservationEntry (type='external_observation'). Admin/Ops notes
// from the monorepo admin-api, read-only in the CRM.
export type TimelineObservationEntry = TimelineEntryBase & {
  type: "external_observation";
  observationId: string;
  body: string;
  source: "admin_ops";
};

// crm: EmailStatus
export type EmailStatus =
  | "queued"
  | "sent"
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "complained"
  | "unsubscribed"
  | "suppressed"
  | "received";

// crm: TimelineEmailEntry (type='email'). Aggregates the whole Mailgun/Gmail
// lifecycle of one email. Body html travels on-demand (hasBodyHtml flag), never
// inline — the v1 panel shows subject + status only.
export type TimelineEmailEntry = TimelineEntryBase & {
  type: "email";
  emailId: string;
  direction: "outbound" | "inbound";
  recipient: string;
  fromAddress: string | null;
  gmailThreadId: string | null;
  subject: string | null;
  hasBodyHtml: boolean;
  templateId: string | null;
  status: EmailStatus;
  hasArchive: boolean;
  openCount: number;
  clickCount: number;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  terminalAt: string | null;
};

// crm: TimelineMilestoneEntry (type='lead_milestone'). Synthesized lifecycle
// milestone; today only 'lead_created' with lead-source attribution.
export type TimelineMilestoneEntry = TimelineEntryBase & {
  type: "lead_milestone";
  milestoneKind: "lead_created";
  leadSource: string | null;
  awarenessSource: string | null;
  awarenessCampaign: string | null;
  awarenessMedium: string | null;
  awarenessLanding: string | null;
};

// crm: TimelineEntry (discriminated union on `type`).
export type TimelineEntry =
  | TimelineMoveEntry
  | TimelineCommentEntry
  | TimelineObservationEntry
  | TimelineEmailEntry
  | TimelineMilestoneEntry;

// crm: Timeline (GET /leads/:id/timeline). `nextCursor` is the opaque cursor of
// the next page (pass it back as `before`), or null when there are no more.
// `pinned` is unpaginated (always complete).
export type Timeline = {
  leadId: string;
  items: TimelineEntry[];
  nextCursor: string | null;
  pinned: TimelineCommentEntry[];
};

// crm: CreateCommentBody (POST /leads/:id/comments). `kind` defaults to
// 'nota_interna' server-side; body is trimmed, 1..4000 chars. Returns 201 Comment.
export type CreateCommentBody = {
  kind?: CommentKind;
  body: string;
};

// crm: Comment (returned by POST /leads/:id/comments).
export type Comment = {
  id: string;
  leadId: string;
  kind: CommentKind;
  body: string;
  bodyHtml?: string | null;
  actor: string;
  actorName: string | null;
  actorPictureUrl: string | null;
  createdAt: string;
};

// crm: RecordingUrl (GET /phone/calls/:callId/recording-url). Signed URL for
// call-recording playback; short-lived (expiresAt).
export type RecordingUrl = {
  url: string;
  expiresAt: string;
  contentType: string | null;
};

// crm: EmailHtmlResponse (GET /leads/:id/emails/:emailId/html). Inline HTML body
// of a timeline email, fetched on-demand. `html` is null when the email row has
// no materialised inline HTML (the caller then shows "sin cuerpo").
export type EmailHtmlResponse = {
  html: string | null;
};

// crm: EmailAttachmentRef / ParsedEmailView / EmailContentUrlResponse
// (GET /leads/:id/emails/:emailId/content-url). Cubre el `.eml` archivado
// (inbound Gmail / transaccional Mailgun): URL firmada al original + vista
// parseada (cuerpo HTML) para render inline. Es la SEGUNDA fuente del cuerpo
// de un email del timeline (la primera es /html con el body_html materializado).
export type EmailAttachmentRef = {
  filename: string;
  contentType: string | null;
  url: string;
};

export type ParsedEmailView = {
  subject: string | null;
  from: string | null;
  to: string | null;
  date: string | null;
  html: string | null;
};

export type EmailContentUrlResponse = {
  url: string;
  expiresAt: string;
  contentType: string | null;
  attachments: EmailAttachmentRef[];
  parsed: ParsedEmailView | null;
};

// ===========================================================================
// Softphone (Fase 7). Mirrors crm/packages/contracts src/phone.ts (StartCall*,
// WebrtcTokenResponse, CallState, AdvisorAvailability, Hold/Transfer bodies)
// and src/preferences.ts (PhonePrefs), plus the realtime token/event shapes of
// crm/apps/api src/modules/realtime + crm/packages/realtime src/events.ts.
// ===========================================================================

// crm: PhoneProvider (phone.ts). Per-call provider switcher of the
// orchestrator. Omitted server-side default = cloudtalk.
export type PhoneProvider = "cloudtalk" | "telnyx";

// crm: PhonePrefs (preferences.ts, scope 'phone'). Absent provider = cloudtalk.
export type PhonePrefs = { provider?: PhoneProvider };

// crm: PreferenceEnvelope (GET/PUT /preferences/:scope response wrapper).
export type PreferenceEnvelope<T> = { scope: string; value: T };

// crm: StartCallBody (POST /leads/:id/call/start). All fields optional.
export type StartCallBody = {
  toNumberE164?: string;
  leadTaskId?: string;
  provider?: PhoneProvider;
};

// crm: 202 response of POST /leads/:id/call/start — { ok:true } + StartCallResponse.
export type StartCallResult = {
  ok: true;
  callId: string;
  providerCallId: string;
  status: PhoneCallStatus;
};

// crm: DialBody (POST /phone/dial). Free dialer: call any number. The server
// normalizes to E.164 (market hint) and resolves the lead by phone_last9.
export type DialMarket = "ES" | "IT" | "FR" | "PT";
export type DialBody = {
  toNumber: string;
  market?: DialMarket;
  provider?: PhoneProvider;
};

// crm: DialResponse (202). Same call lifecycle as call/start; leadId is the
// matched lead (hex 32) or null if the number belongs to no lead.
export type DialResponse = {
  ok: true;
  callId: string;
  providerCallId: string;
  status: PhoneCallStatus;
  leadId: string | null;
};

// crm: PhoneResolveResponse (GET /phone/resolve?phone=). Resolves a number to a
// lead for the floating console (name + market + canonical-ready leadId hex32).
export type PhoneResolveResponse = {
  leadId: string | null;
  leadName: string | null;
  market: string | null;
  phone: string | null;
};

// crm: WebrtcTokenResponse (POST /phone/webrtc-token). login_token of the
// Telnyx SDK (~24h) minted per-agent by the orchestrator via the CRM proxy.
export type WebrtcTokenResponse = {
  token: string;
  sipUsername: string;
  expiresInSec: number;
};

// crm: CallState (phone.ts). Presence call-state the softphone reports to the
// CRM (POST /presence/call-state) and what /advisors/availability returns.
export type CallState = "idle" | "ringing" | "on_call";

// crm: AdvisorAvailability (GET /advisors/availability -> { items }). Live
// availability for the transfer picker: online (recent heartbeat) + callState.
export type AdvisorAvailability = {
  email: string;
  name: string | null;
  pictureUrl: string | null;
  market: string | null;
  online: boolean;
  callState: CallState;
};

// crm: TransferConsultResponse (POST /phone/calls/:id/transfer/consult).
// consultCallControlId is the target advisor's leg — kept client-side for
// complete/cancel (the server also seals it, so it is optional on those).
export type TransferConsultResponse = {
  ok: boolean;
  consultCallControlId: string;
  targetAgentEmail: string;
};

// crm: TransferCompleteBody (POST /phone/calls/:id/transfer/complete). All
// optional — a missing consultCallControlId is recovered from the server seal.
export type TransferCompleteBody = {
  consultCallControlId?: string;
  targetAgentEmail?: string;
  leadId?: string;
};

// crm: RealtimeTokenResponse (GET /realtime/token). The BFF discovers the
// Mercure hub for the browser: `hubUrl` is where EventSource connects (with
// the httpOnly `mercureAuthorization` cookie the same response sets) and
// `enabled` is false when the deployment has no hub configured. No env var
// needed in this SPA: hub discovery is runtime via this endpoint.
export type RealtimeTokenResponse = {
  enabled: boolean;
  hubUrl: string | null;
  topics: string[];
  expiresIn: number;
  token?: string;
};

// crm: live call status event (packages/realtime src/events.ts), published by
// crm/apps/jobs on the lead topic when the orchestrator reports lifecycle
// transitions. Mapping: initiated=sonando, answered=en llamada, completed=colgada.
export type PhoneCallLiveStatus = "initiated" | "answered" | "completed";

export type PhoneCallStatusEvent = {
  type: "phone.call.status";
  leadId: string;
  callId: string;
  status: PhoneCallLiveStatus;
  phone: string | null;
};

// crm: tickets de impago/disputa que la vista Cobranza (Operations) cruza en
// cliente contra el estado de cobro de Stripe. Contrato de
// GET /tickets/payment-issues?months=...
export type PaymentIssueTicket = {
  leadId: string;
  ticketId: string;
  pipeline: string;
  incidentType: string | null;
  typification: string | null;
  state: string | null;
  createdAt: string;
  month: string | null;
};

export type PaymentIssuesResponse = {
  items: PaymentIssueTicket[];
};
